/**
 * Spec 107 — ThreeLensPanel.
 *
 * Shows the three COEXISTING lenses for one page + persona side by side, each
 * labeled with its own claim type, none overriding another and NONE blended into
 * a single number:
 *   - GEO rules (083)            — compliance, audience-agnostic
 *   - Response persona-fit (106) — observational (where available)
 *   - Page persona-fit (107)     — content-quality hypothesis
 *
 * This panel is the read-only entry point to the 108/109 cross-lens diagnosis.
 *
 * data-testids: three-lens-panel, lens-geo, lens-response-persona,
 * lens-page-persona, lens-claim-type.
 */
import type { ThreeLensComposition } from '../api/pagePersonaEvaluations.js';

function pct(n: number | null): string {
  return n == null ? '—' : `${Math.round(n * 100)}%`;
}
function num(n: number | null): string {
  return n == null ? '—' : n.toFixed(2);
}

const cellClass =
  'flex-1 min-w-[200px] rounded-lg border border-slate-200 bg-white p-3 ';

const claimClass =
  'inline-block rounded-full px-2 py-0.5 text-xs font-medium mb-2';

export function ThreeLensPanel({
  composition,
}: {
  composition: ThreeLensComposition | null;
}) {
  if (!composition) return null;
  const { geo, response_persona_fit: rpf, page_persona_fit: ppf } = composition;

  return (
    <div
      data-testid="three-lens-panel"
      className="three-lens-panel flex flex-wrap gap-3"
    >
      {/* Lens 1 — GEO rules (083). */}
      <div data-testid="lens-geo" className={cellClass}>
        <span
          data-testid="lens-claim-type"
          className={`${claimClass} bg-sky-50 text-sky-700 border border-sky-200`}
        >
          Compliance · audience-agnostic
        </span>
        <h4 className="text-sm font-semibold text-slate-900 mb-1.5 mt-0">GEO rules</h4>
        {geo ? (
          <p className="text-sm text-slate-600 m-0">
            Score <strong>{num(geo.aggregate_score)}</strong> · {geo.rules_evaluated} rules ·{' '}
            {geo.status}
          </p>
        ) : (
          <p className="text-sm text-slate-400 m-0">No GEO evaluation yet.</p>
        )}
      </div>

      {/* Lens 2 — Response persona-fit (106). */}
      <div data-testid="lens-response-persona" className={cellClass}>
        <span
          data-testid="lens-claim-type"
          className={`${claimClass} bg-violet-50 text-violet-700 border border-violet-200`}
        >
          Observational
        </span>
        <h4 className="text-sm font-semibold text-slate-900 mb-1.5 mt-0">AI-answer persona-fit</h4>
        {rpf ? (
          <p className="text-sm text-slate-600 m-0">
            Visibility <strong>{pct(rpf.observed_visibility)}</strong> · priority{' '}
            {num(rpf.priority_fit)} · trust {num(rpf.trust)} · {rpf.status}
          </p>
        ) : (
          <p className="text-sm text-slate-400 m-0">
            No AI-answer persona-fit for this persona yet.
          </p>
        )}
      </div>

      {/* Lens 3 — Page persona-fit (107, this spec). */}
      <div data-testid="lens-page-persona" className={cellClass}>
        <span
          data-testid="lens-claim-type"
          className={`${claimClass} bg-amber-50 text-amber-700 border border-amber-200`}
        >
          Content-quality hypothesis
        </span>
        <h4 className="text-sm font-semibold text-slate-900 mb-1.5 mt-0">Page persona-fit</h4>
        {ppf ? (
          ppf.status === 'unevaluable' ? (
            <p className="text-sm text-amber-700 m-0">
              Couldn’t evaluate — {ppf.unevaluable_reason ?? 'no usable content'}.
            </p>
          ) : (
            <p className="text-sm text-slate-600 m-0">
              Priority <strong>{num(ppf.page_priority_fit_score)}</strong>
              {ppf.page_wellbeing_fit_score != null && (
                <> · wellbeing {num(ppf.page_wellbeing_fit_score)}</>
              )}{' '}
              · trust-provision {num(ppf.trust_provision_score)} · {ppf.status}
            </p>
          )
        ) : (
          <p className="text-sm text-slate-400 m-0">
            No page persona-fit yet.
          </p>
        )}
      </div>
    </div>
  );
}
